import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useBoardsContext } from '../context/BoardsContext'
import { initTemplatePicker } from '../../../lib/template_picker'

const TRIGGER_LABELS = {
  status_entered: 'Job enters status',
  status_left: 'Job leaves status',
  job_assigned: 'Assignee added',
  requester_added: 'Requester added',
  technician_added: 'Technician added',
  coordinator_added: 'Coordinator added',
  card_entered_column: 'Job added/moved to column',
  job_late: 'Job is late'
}

const TRIGGER_SHORT = {
  status_entered: 'ENTERS',
  status_left: 'LEAVES',
  job_assigned: 'ASSIGN',
  requester_added: 'REQ',
  technician_added: 'TECH',
  coordinator_added: 'COORD',
  card_entered_column: 'ADDED',
  job_late: 'LATE'
}

const MEMBER_TRIGGERS = ['job_assigned', 'requester_added', 'technician_added', 'coordinator_added']
const STATUS_TRIGGERS = ['status_entered', 'status_left']

const ACTION_LABELS = {
  add_card: 'Add card to column',
  remove_card: 'Remove card from column(s)',
  change_status: 'Change job status',
  set_member: 'Set job member'
}

const MEMBER_ROLE_LABELS = {
  assignee: 'Assignee',
  requester: 'Requester',
  technician: 'Technician',
  coordinator: 'Coordinator'
}

function describeAutomation(a) {
  const trigger = TRIGGER_LABELS[a.trigger_type] || a.trigger_type
  let triggerDetail
  if (MEMBER_TRIGGERS.includes(a.trigger_type)) {
    triggerDetail = a.trigger_user_name
  } else if (a.trigger_type === 'card_entered_column') {
    triggerDetail = a.trigger_column_name
  } else if (a.trigger_type === 'job_late') {
    triggerDetail = a.excluded_status_names?.length
      ? `not in ${a.excluded_status_names.join(', ')}`
      : 'any status'
  } else {
    triggerDetail = a.trigger_status_name
  }

  let action
  if (a.action_type === 'add_card') {
    action = `add to "${a.target_column_name}"`
  } else if (a.action_type === 'remove_card') {
    action = a.target_column_ids?.length ? `remove from ${a.target_column_ids.length} column(s)` : 'remove from all columns'
  } else if (a.action_type === 'change_status') {
    action = `set status to "${a.target_status_name}"`
  } else if (a.action_type === 'set_member') {
    action = `set ${a.member_role} to "${a.member_user_name}"`
  }

  const scopeParts = []
  if (a.location_names?.length) scopeParts.push(a.location_names.join(', '))
  if (a.parent_template_names?.length) scopeParts.push(a.parent_template_names.join(', '))
  const scope = scopeParts.length ? ` (${scopeParts.join('; ')})` : ''
  return `When ${trigger.toLowerCase()} "${triggerDetail}" → ${action}${scope}`
}

function UserSelect({ value, onChange }) {
  const selectRef = useRef(null)
  const tsRef = useRef(null)

  useEffect(() => {
    if (!selectRef.current || !window.TomSelect) return
    if (tsRef.current) return

    tsRef.current = new window.TomSelect(selectRef.current, {
      placeholder: 'Search for a user...',
      allowEmptyOption: true,
      valueField: 'id',
      labelField: 'name_and_email',
      searchField: 'name_and_email',
      load: (query, callback) => {
        const encoded = encodeURIComponent(query)
        fetch(`/users?query=${encoded}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
          .then(response => response.json())
          .then(users => callback(users))
          .catch(() => callback())
      }
    })

    tsRef.current.on('change', (val) => {
      onChange(val)
    })

    return () => {
      if (tsRef.current) {
        tsRef.current.destroy()
        tsRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (tsRef.current && tsRef.current.getValue() !== value) {
      tsRef.current.setValue(value, true)
    }
  }, [value])

  return <select ref={selectRef} defaultValue={value} />
}

// Template scope: the shared template picker (lib/template_picker.js) over a
// <select multiple> built from the options query's template_groups. The NA
// sentinel ("jobs with no template") sits first, ungrouped. Values are the
// strings TomSelect holds; the server casts ids and keeps 'NA'.
function TemplateScopeSelect({ groups, value, onChange }) {
  const selectRef = useRef(null)
  const tsRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const optionAttrs = (t) => ({
    'data-jobs': t.jobs, 'data-recent': t.recent, 'data-last': t.last || undefined, 'data-on-form': t.onForm, 'data-inactive': t.inactive
  })
  const selected = (value || []).map(String)
  const mostUsed = groups.most_used || []
  const keptInactive = (groups.inactive || []).filter((t) => selected.includes(String(t.value)))
  const reachable = (groups.inactive || []).filter((t) => !selected.includes(String(t.value)))
  const total = mostUsed.length + (groups.active || []).length + (groups.inactive || []).length
  const windowDays = groups.window_days || 90

  useEffect(() => {
    if (!selectRef.current || tsRef.current) return undefined
    tsRef.current = initTemplatePicker(selectRef.current, { placeholder: 'Add a template…' })
    tsRef.current.setValueKept(selected, true)
    tsRef.current.on('change', (val) => {
      const list = Array.isArray(val) ? val : (val ? [val] : [])
      onChangeRef.current(list.map(String))
    })
    return () => {
      if (tsRef.current) {
        tsRef.current.destroy()
        tsRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const ts = tsRef.current
    if (!ts) return
    const current = (ts.getValue() || []).map(String).sort().join(',')
    if (current !== [...selected].sort().join(',')) ts.setValueKept(selected, true)
  }, [selected.join(',')])

  return (
    <select
      ref={selectRef}
      multiple
      data-template-picker
      data-template-picker-inactive={JSON.stringify(reachable)}
      data-template-picker-total={total}
      data-template-picker-window={windowDays}
      defaultValue={selected}
    >
      <option value="NA" data-meta="jobs with no template">NA</option>
      <optgroup label={`Most used, last ${windowDays} days`} data-value="most_used">
        {mostUsed.map((t) => <option key={t.value} value={t.value} {...optionAttrs(t)}>{t.text}</option>)}
      </optgroup>
      <optgroup label={mostUsed.length ? 'Other active templates, A to Z' : 'Active templates, A to Z'} data-value="active">
        {(groups.active || []).map((t) => <option key={t.value} value={t.value} {...optionAttrs(t)}>{t.text}</option>)}
      </optgroup>
      <optgroup label="Inactive" data-value="inactive">
        {keptInactive.map((t) => <option key={t.value} value={t.value} {...optionAttrs(t)}>{t.text}</option>)}
      </optgroup>
    </select>
  )
}

TemplateScopeSelect.propTypes = {
  groups: PropTypes.shape({
    most_used: PropTypes.array, active: PropTypes.array, inactive: PropTypes.array, window_days: PropTypes.number
  }).isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

const DEFAULT_FORM_STATE = {
  triggerType: 'status_entered',
  actionType: 'add_card',
  triggerStatusId: '',
  triggerUserId: '',
  targetColumnId: '',
  targetColumnIds: [],
  allColumns: true,
  triggerColumnId: '',
  targetStatusId: '',
  memberRole: 'assignee',
  memberUserId: '',
  allLocations: true,
  locationIds: [],
  allTemplates: true,
  parentTemplateIds: [],
  excludedStatusIds: []
}

function automationToFormState(a) {
  return {
    triggerType: a.trigger_type,
    actionType: a.action_type,
    triggerStatusId: a.trigger_status_id != null ? String(a.trigger_status_id) : '',
    triggerUserId: a.trigger_user_id != null ? String(a.trigger_user_id) : '',
    targetColumnId: a.target_column_id != null ? String(a.target_column_id) : '',
    targetColumnIds: a.target_column_ids || [],
    allColumns: !(a.target_column_ids && a.target_column_ids.length > 0),
    triggerColumnId: a.trigger_column_id != null ? String(a.trigger_column_id) : '',
    targetStatusId: a.target_status_id != null ? String(a.target_status_id) : '',
    memberRole: a.member_role || 'assignee',
    memberUserId: a.member_user_id != null ? String(a.member_user_id) : '',
    allLocations: !(a.location_ids && a.location_ids.length > 0),
    locationIds: a.location_ids || [],
    allTemplates: !(a.parent_template_ids && a.parent_template_ids.length > 0),
    parentTemplateIds: a.parent_template_ids || [],
    excludedStatusIds: a.excluded_status_ids || []
  }
}

function canonicalForm(s) {
  const sortNum = (arr) => [...(arr || [])].map(Number).sort((a, b) => a - b)
  // Template scope can include the non-numeric 'NA' sentinel, so sort as strings.
  const sortMixed = (arr) => [...(arr || [])].map(String).sort()
  return JSON.stringify({
    ...s,
    targetColumnIds: sortNum(s.targetColumnIds),
    locationIds: sortNum(s.locationIds),
    parentTemplateIds: sortMixed(s.parentTemplateIds),
    excludedStatusIds: sortNum(s.excludedStatusIds)
  })
}

const DEFAULT_CANONICAL = canonicalForm(DEFAULT_FORM_STATE)

// Options payload from a server that predates template_groups: one flat
// active list, no usage. Only reachable mid-deploy.
function templateGroupsFromFlat(list) {
  const active = (list || []).filter((t) => t.id !== 'NA').map((t) => ({ value: String(t.id), text: t.name, jobs: 0, recent: 0, onForm: 0, inactive: 0 }))
  return { most_used: [], active, inactive: [], window_days: 90 }
}

function AutomationsModal({ onClose }) {
  const { selectedBoardId } = useBoardsContext()
  const queryClient = useQueryClient()

  const [editingId, setEditingId] = useState(null)
  const [initialSnapshot, setInitialSnapshot] = useState(DEFAULT_CANONICAL)
  const [triggerType, setTriggerType] = useState('status_entered')
  const [actionType, setActionType] = useState('add_card')
  const [triggerStatusId, setTriggerStatusId] = useState('')
  const [triggerUserId, setTriggerUserId] = useState('')
  const [targetColumnId, setTargetColumnId] = useState('')
  const [targetColumnIds, setTargetColumnIds] = useState([])
  const [allColumns, setAllColumns] = useState(true)
  const [triggerColumnId, setTriggerColumnId] = useState('')
  const [targetStatusId, setTargetStatusId] = useState('')
  const [memberRole, setMemberRole] = useState('assignee')
  const [memberUserId, setMemberUserId] = useState('')
  const [allLocations, setAllLocations] = useState(true)
  const [locationIds, setLocationIds] = useState([])
  const [allTemplates, setAllTemplates] = useState(true)
  const [parentTemplateIds, setParentTemplateIds] = useState([])
  const [excludedStatusIds, setExcludedStatusIds] = useState([])

  const automationsQuery = useQuery({
    queryKey: ['kanbanAutomations', selectedBoardId],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', `/kanban_boards/${selectedBoardId}/kanban_board_automations`, {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      return response.json
    },
    enabled: !!selectedBoardId
  })

  const optionsQuery = useQuery({
    queryKey: ['kanbanAutomationOptions', selectedBoardId],
    queryFn: async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', `/kanban_boards/${selectedBoardId}/automation_options`, {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      return response.json
    },
    enabled: !!selectedBoardId
  })

  const createAutomation = useMutation({
    mutationFn: async (data) => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('post', `/kanban_boards/${selectedBoardId}/kanban_board_automations`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ kanban_board_automation: data })
      })
      const response = await request.perform()
      return response.json
    },
    onSuccess: () => automationsQuery.refetch()
  })

  const updateAutomation = useMutation({
    mutationFn: async ({ id, data }) => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('put', `/kanban_boards/${selectedBoardId}/kanban_board_automations/${id}`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ kanban_board_automation: data })
      })
      const response = await request.perform()
      return response.json
    },
    onSuccess: () => automationsQuery.refetch()
  })

  const toggleAutomation = useMutation({
    mutationFn: async ({ id, active }) => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('put', `/kanban_boards/${selectedBoardId}/kanban_board_automations/${id}`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ kanban_board_automation: { active } })
      })
      const response = await request.perform()
      return response.json
    },
    onSuccess: () => automationsQuery.refetch()
  })

  const deleteAutomation = useMutation({
    mutationFn: async (id) => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('delete', `/kanban_boards/${selectedBoardId}/kanban_board_automations/${id}`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      const response = await request.perform()
      return response.json
    },
    onSuccess: () => automationsQuery.refetch()
  })

  const automations = automationsQuery.data?.automations || []
  const options = optionsQuery.data || { statuses: [], users: [], locations: [], templates: [], columns: [], locations_enabled: false, templates_enabled: false, member_types: { assignee: true } }

  const currentFormState = {
    triggerType, actionType, triggerStatusId, triggerUserId, targetColumnId,
    targetColumnIds, allColumns, triggerColumnId, targetStatusId,
    memberRole, memberUserId, allLocations, locationIds, allTemplates,
    parentTemplateIds, excludedStatusIds
  }
  const isDirty = canonicalForm(currentFormState) !== initialSnapshot

  const applyFormState = (s) => {
    setTriggerType(s.triggerType)
    setActionType(s.actionType)
    setTriggerStatusId(s.triggerStatusId)
    setTriggerUserId(s.triggerUserId)
    setTargetColumnId(s.targetColumnId)
    setTargetColumnIds(s.targetColumnIds)
    setAllColumns(s.allColumns)
    setTriggerColumnId(s.triggerColumnId)
    setTargetStatusId(s.targetStatusId)
    setMemberRole(s.memberRole)
    setMemberUserId(s.memberUserId)
    setAllLocations(s.allLocations)
    setLocationIds(s.locationIds)
    setAllTemplates(s.allTemplates)
    setParentTemplateIds(s.parentTemplateIds)
    setExcludedStatusIds(s.excludedStatusIds)
  }

  const resetForm = () => {
    applyFormState(DEFAULT_FORM_STATE)
    setEditingId(null)
    setInitialSnapshot(DEFAULT_CANONICAL)
  }

  const startEditing = (automation) => {
    if (editingId === automation.id) return
    if (isDirty && !window.confirm('Discard your unsaved changes and edit this rule?')) return
    const target = automationToFormState(automation)
    applyFormState(target)
    setEditingId(automation.id)
    setInitialSnapshot(canonicalForm(target))
  }

  const cancelEditing = () => {
    if (isDirty && !window.confirm('Discard your changes?')) return
    resetForm()
  }

  const handleTriggerTypeChange = (val) => {
    setTriggerType(val)
    if (val === 'status_left') setActionType('remove_card')
    else if (MEMBER_TRIGGERS.includes(val)) setActionType('add_card')
    else if (val === 'card_entered_column') setActionType('change_status')
    else if (val === 'job_late') setActionType('add_card')
    else setActionType('add_card')
  }

  const handleColumnToggle = (colId) => {
    setTargetColumnIds(prev =>
      prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]
    )
  }

  const handleSubmit = () => {
    const data = {
      trigger_type: triggerType,
      action_type: actionType
    }

    if (MEMBER_TRIGGERS.includes(triggerType)) {
      data.trigger_user_id = Number(triggerUserId)
    } else if (triggerType === 'card_entered_column') {
      data.trigger_column_id = Number(triggerColumnId)
    } else if (triggerType === 'job_late') {
      data.excluded_status_ids = excludedStatusIds
    } else {
      data.trigger_status_id = Number(triggerStatusId)
    }

    if (actionType === 'add_card') {
      data.target_column_id = Number(targetColumnId)
    } else if (actionType === 'remove_card') {
      data.target_column_ids = allColumns ? [] : targetColumnIds
    } else if (actionType === 'change_status') {
      data.target_status_id = Number(targetStatusId)
    } else if (actionType === 'set_member') {
      data.member_role = memberRole
      data.member_user_id = Number(memberUserId)
    }

    // Persist explicit empty arrays when in edit mode so the backend overwrites
    // previously-set scopes; on create, omit empty arrays as before.
    if (editingId) {
      data.location_ids = !allLocations ? locationIds : []
      data.parent_template_ids = !allTemplates ? parentTemplateIds : []
    } else {
      if (!allLocations && locationIds.length) data.location_ids = locationIds
      if (!allTemplates && parentTemplateIds.length) data.parent_template_ids = parentTemplateIds
    }

    if (editingId) {
      updateAutomation.mutate({ id: editingId, data }, { onSuccess: resetForm })
    } else {
      createAutomation.mutate(data, { onSuccess: resetForm })
    }
  }

  const isValid = () => {
    const isMemberTrigger = MEMBER_TRIGGERS.includes(triggerType)
    if (isMemberTrigger && !triggerUserId) return false
    if (STATUS_TRIGGERS.includes(triggerType) && !triggerStatusId) return false
    if (triggerType === 'card_entered_column' && !triggerColumnId) return false
    if (actionType === 'add_card' && !targetColumnId) return false
    if (actionType === 'remove_card' && !allColumns && targetColumnIds.length === 0) return false
    if (actionType === 'change_status' && !targetStatusId) return false
    if (actionType === 'set_member' && (!memberRole || !memberUserId)) return false
    if (options.locations_enabled && !allLocations && locationIds.length === 0) return false
    if (options.templates_enabled && !allTemplates && parentTemplateIds.length === 0) return false
    return true
  }

  const memberTypes = options.member_types || {}
  const TRIGGER_MEMBER_MAP = {
    job_assigned: 'assignee',
    requester_added: 'requester',
    technician_added: 'technician',
    coordinator_added: 'coordinator'
  }

  const availableTriggers = Object.entries(TRIGGER_LABELS).filter(([key]) => {
    if (TRIGGER_MEMBER_MAP[key]) return memberTypes[TRIGGER_MEMBER_MAP[key]]
    return true
  })

  const getActionOptions = () => {
    if (triggerType === 'status_left') return [['remove_card', ACTION_LABELS.remove_card]]
    if (MEMBER_TRIGGERS.includes(triggerType)) return [['add_card', ACTION_LABELS.add_card]]
    if (triggerType === 'card_entered_column') return [['change_status', ACTION_LABELS.change_status], ['set_member', ACTION_LABELS.set_member]]
    if (triggerType === 'job_late') return [['add_card', ACTION_LABELS.add_card]]
    return [['add_card', ACTION_LABELS.add_card], ['remove_card', ACTION_LABELS.remove_card]]
  }
  const actionOptions = getActionOptions()

  const availableMemberRoles = [
    ['assignee', 'Assignee'],
    ...(memberTypes.requester ? [['requester', 'Requester']] : []),
    ...(memberTypes.technician ? [['technician', 'Technician']] : []),
    ...(memberTypes.coordinator ? [['coordinator', 'Coordinator']] : [])
  ]

  const ruleCount = automations.length
  const activeCount = automations.filter(a => a.active).length

  return (
    <div className="kb-modal kb-auto fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="kb-auto-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kb-auto-header">
          <div className="kb-auto-header-title">
            <h3>Automations</h3>
            <span className="kb-auto-header-count">
              {ruleCount} {ruleCount === 1 ? 'rule' : 'rules'}{ruleCount > 0 && ` · ${activeCount} active`}
            </span>
          </div>
          <button className="kb-auto-close" onClick={onClose} title="Close" data-tour="automation-close">
            &times;
          </button>
        </div>

        <div className="kb-auto-body">
          {/* LEFT — list */}
          <div className="kb-auto-list" data-tour="automation-rules">
            <div className="kb-auto-list-scroll">
              {automations.length === 0 && (
                <div className="kb-auto-list-empty">No automations yet</div>
              )}
              {automations.map(a => {
                const isEditing = editingId === a.id
                return (
                  <div
                    key={a.id}
                    className={`kb-auto-rule ${a.active ? '' : 'kb-auto-rule--off'} ${isEditing ? 'kb-auto-rule--editing' : ''}`}
                  >
                    <div className="kb-auto-rule-main">
                      <span className="kb-auto-rule-tag">{TRIGGER_SHORT[a.trigger_type] || a.trigger_type}</span>
                      <span className="kb-auto-rule-text">{describeAutomation(a)}</span>
                    </div>
                    <div className="kb-auto-rule-actions">
                      <button
                        className={`kb-auto-rule-edit ${isEditing ? 'is-active' : ''}`}
                        onClick={() => startEditing(a)}
                        title={isEditing ? 'Currently editing' : 'Edit rule'}
                      >
                        ✎
                      </button>
                      <button
                        className={`kb-auto-rule-toggle ${a.active ? 'is-on' : 'is-off'}`}
                        onClick={() => toggleAutomation.mutate({ id: a.id, active: !a.active })}
                        title={a.active ? 'Turn off' : 'Turn on'}
                      >
                        {a.active ? 'On' : 'Off'}
                      </button>
                      <button
                        className="kb-auto-rule-delete"
                        onClick={() => {
                          if (editingId === a.id) resetForm()
                          deleteAutomation.mutate(a.id)
                        }}
                        title="Delete rule"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="kb-auto-form" data-tour="automation-form">
            <div className="kb-auto-form-eyebrow">
              <span>{editingId ? 'Editing rule' : 'New rule'}</span>
              {editingId && (
                <button className="kb-auto-cancel-edit" onClick={cancelEditing} title="Cancel edit">
                  &times; cancel
                </button>
              )}
            </div>

            <div className="kb-auto-form-scroll space-y-3">
              {/* Trigger type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">When</label>
                <select
                  className="form-select w-full text-sm rounded border-gray-300"
                  value={triggerType}
                  onChange={(e) => handleTriggerTypeChange(e.target.value)}
                >
                  {availableTriggers.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Trigger config */}
              {MEMBER_TRIGGERS.includes(triggerType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                  <UserSelect value={triggerUserId} onChange={setTriggerUserId} />
                </div>
              )}
              {STATUS_TRIGGERS.includes(triggerType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    className="form-select w-full text-sm rounded border-gray-300"
                    value={triggerStatusId}
                    onChange={(e) => setTriggerStatusId(e.target.value)}
                  >
                    <option value="">Select status...</option>
                    {options.statuses.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {triggerType === 'card_entered_column' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Column</label>
                  <select
                    className="form-select w-full text-sm rounded border-gray-300"
                    value={triggerColumnId}
                    onChange={(e) => setTriggerColumnId(e.target.value)}
                  >
                    <option value="">Select column...</option>
                    {options.columns.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {triggerType === 'job_late' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Excluded statuses</label>
                  <p className="text-xs text-gray-500 mb-2">
                    The automation runs only when the job&apos;s status is NOT one of these. Leave empty to run for any status.
                  </p>
                  <div className="space-y-1 ml-1">
                    {options.statuses.map(s => (
                      <div key={s.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={excludedStatusIds.includes(s.id)}
                          onChange={() => setExcludedStatusIds(prev =>
                            prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]
                          )}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">{s.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Then</label>
                <select
                  className="form-select w-full text-sm rounded border-gray-300"
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                >
                  {actionOptions.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Action config */}
              {actionType === 'add_card' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Column</label>
                  <select
                    className="form-select w-full text-sm rounded border-gray-300"
                    value={targetColumnId}
                    onChange={(e) => setTargetColumnId(e.target.value)}
                  >
                    <option value="">Select column...</option>
                    {options.columns.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {actionType === 'remove_card' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From columns</label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="all-columns"
                      checked={allColumns}
                      onChange={(e) => setAllColumns(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <label htmlFor="all-columns" className="text-sm text-gray-700">All columns</label>
                  </div>
                  {!allColumns && (
                    <div className="space-y-1 ml-4">
                      {options.columns.map(c => (
                        <div key={c.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={targetColumnIds.includes(c.id)}
                            onChange={() => handleColumnToggle(c.id)}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-sm text-gray-700">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {actionType === 'change_status' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Set status to</label>
                  <select
                    className="form-select w-full text-sm rounded border-gray-300"
                    value={targetStatusId}
                    onChange={(e) => setTargetStatusId(e.target.value)}
                  >
                    <option value="">Select status...</option>
                    {options.statuses.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {actionType === 'set_member' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      className="form-select w-full text-sm rounded border-gray-300"
                      value={memberRole}
                      onChange={(e) => setMemberRole(e.target.value)}
                    >
                      {availableMemberRoles.map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                    <UserSelect value={memberUserId} onChange={setMemberUserId} />
                  </div>
                </>
              )}

              {/* Location scope */}
              {options.locations_enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location scope</label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="all-locations"
                      checked={allLocations}
                      onChange={(e) => { setAllLocations(e.target.checked); if (e.target.checked) setLocationIds([]) }}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <label htmlFor="all-locations" className="text-sm text-gray-700">All locations</label>
                  </div>
                  {!allLocations && (
                    <div className="space-y-1 ml-4">
                      {(options.locations || []).map(l => (
                        <div key={l.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={locationIds.includes(l.id)}
                            onChange={() => setLocationIds(prev =>
                              prev.includes(l.id) ? prev.filter(id => id !== l.id) : [...prev, l.id]
                            )}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-sm text-gray-700">{l.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Template scope */}
              {options.templates_enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template scope</label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="all-templates"
                      checked={allTemplates}
                      onChange={(e) => { setAllTemplates(e.target.checked); if (e.target.checked) setParentTemplateIds([]) }}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <label htmlFor="all-templates" className="text-sm text-gray-700">All templates</label>
                  </div>
                  {!allTemplates && (
                    <div className="ml-4">
                      <TemplateScopeSelect
                        groups={options.template_groups || templateGroupsFromFlat(options.templates)}
                        value={parentTemplateIds}
                        onChange={setParentTemplateIds}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Most used templates first. Type to find any template, inactive ones included.
                      </p>
                    </div>
                  )}
                </div>
              )}

            </div>

            <button
              className="kb-auto-submit"
              onClick={handleSubmit}
              disabled={!isValid() || createAutomation.isPending || updateAutomation.isPending}
            >
              {editingId
                ? (updateAutomation.isPending ? 'Saving…' : 'Save changes')
                : (createAutomation.isPending ? 'Adding…' : 'Add Automation')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

AutomationsModal.propTypes = {
  onClose: PropTypes.func.isRequired
}

export default AutomationsModal
